import * as THREE from 'three';

import {OperationGroup} from '../../Preview3D/types';
import {
    addDragEffectOnClick,
    attachSegmentLineWireframeToggle,
    loadTexture,
    notifyManagerInstantLoad,
    setSegmentLineTransparency,
} from '../../Preview3D/helpers/Three';

/**
 * Which face panels are present on this kicker / bulkhead assembly.
 * When a face is absent, the panel end that would join it stays square
 * (no miter cut).
 */
export type BevelFaceFlags = {
    hasFrontFace: boolean;
    hasBackFace: boolean;
    hasLeftFace: boolean;
    hasRightFace: boolean;
};

// Ladder-frame / shelf inserts are generated by a Jinja `{% for frame in X.inserts %}`
// loop in both the KickerFramed and Bulkhead XML templates.  The resulting
// OperationGroup IDs have the form "IDOP-6-{number}" (e.g. "IDOP-6-250" or
// "IDOP-6-283.5").  Regular structural panels have either plain IDs ("IDOP-1")
// or text-suffix IDs ("IDOP-1-ExteriorLeft"), neither of which ends with a
// bare number after the last hyphen.
const isLoopGeneratedFrameInsert = (og: OperationGroup): boolean => {
    const parts = og.ID.split('-');
    return parts.length > 2 && /^\d+\.?\d*$/.test(parts[parts.length - 1]);
};

// True for structural kicker/bulkhead panels that should receive the
// joining-edge bevel.  Excludes shelves, partitions, drawer parts, and
// loop-generated ladder-frame / shelf inserts.
export const isBeveledFramePanel = (og: OperationGroup): boolean =>
    !og.IsShelf &&
    !og.IsShelfPartition &&
    !og.IsDrawerFront &&
    !og.IsDoorPullout &&
    !og.IsDrawerRunner &&
    !og.IsDrawerBottom &&
    !og.IsDrawerBack &&
    !isLoopGeneratedFrameInsert(og);

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

// Determine which ends of a structural panel should receive a 45° miter cut.
//
// The face normal of the panel (from its first `panel`-type operation) tells us
// the panel's orientation inside the kicker / bulkhead assembly:
//
//   X-normal panels (nx ≠ 0): side panels running front ↔ back.
//     yMin end faces the back  face → bevel only when hasBackFace
//     yMax end faces the front face → bevel only when hasFrontFace
//
//   Y-normal panels (ny ≠ 0): front / back face panels running left ↔ right.
//     yMin end faces the right side → bevel only when hasRightFace
//     yMax end faces the left  side → bevel only when hasLeftFace
//
//   Z-normal (or unknown): horizontal / other orientation → bevel both ends.
const getPanelBevelEnds = (
    og: OperationGroup,
    flags: BevelFaceFlags
): {bevelAtYMin: boolean; bevelAtYMax: boolean} => {
    // Only suppress miters on exterior cladding panels.  Interior structural
    // panels always have an adjacent neighbour on every end, so they keep full
    // miters regardless of which face panels are present.
    if (!og.ExteriorMaterial) {
        return {bevelAtYMin: true, bevelAtYMax: true};
    }

    // Prefer the first 'panel'-type operation for the face normal; fall back to
    // Operations[0] in case the panel-type operation is absent.
    const panelOp =
        og.Operations.find((op) => op.Type === 'panel') ?? og.Operations[0];
    const normal = panelOp?.Segments?.[0]?.StartNormal;
    const nx = normal?.x ?? 0;
    const ny = normal?.y ?? 0;

    if (Math.abs(nx) > 0.5) {
        // Left side (nx > 0) or right side (nx < 0).
        // After panelGroup.rotateZ(PI/2) + applyOperationGroupTransform the
        // shape's y axis is mirrored, so yMin sits at the BACK end and
        // yMax sits at the FRONT end.
        return {
            bevelAtYMin: flags.hasBackFace,
            bevelAtYMax: flags.hasFrontFace,
        };
    }
    if (Math.abs(ny) > 0.5) {
        // Front face (ny > 0) or back face (ny < 0).
        // After panelGroup.rotateZ(PI/2) + applyOperationGroupTransform the
        // shape's y axis is mirrored, so yMin sits at the RIGHT side and
        // yMax sits at the LEFT side.
        return {
            bevelAtYMin: flags.hasRightFace,
            bevelAtYMax: flags.hasLeftFace,
        };
    }
    // Horizontal / other orientation – bevel both ends.
    return {bevelAtYMin: true, bevelAtYMax: true};
};

// Re-compute the bounding box of the 2D shape segments.
const getShapeBounds = (operation: {
    Segments: Array<{
        StartCoor: {x?: number; y?: number};
        EndCoor: {x?: number; y?: number};
    }>;
}) => {
    let xMin = Infinity;
    let xMax = -Infinity;
    let yMin = Infinity;
    let yMax = -Infinity;

    for (const seg of operation.Segments) {
        const sx = seg.StartCoor.x ?? 0;
        const sy = seg.StartCoor.y ?? 0;
        const ex = seg.EndCoor.x ?? 0;
        const ey = seg.EndCoor.y ?? 0;
        xMin = Math.min(xMin, sx, ex);
        xMax = Math.max(xMax, sx, ex);
        yMin = Math.min(yMin, sy, ey);
        yMax = Math.max(yMax, sy, ey);
    }

    // The XML templates use y=1 (and sometimes x=1) as a butt-joint guard to
    // avoid z-fighting with the adjacent panel.  For miter geometry the back-
    // face corners must coincide exactly with the neighbouring panel's corners,
    // so we normalise any sub-2 mm offset at the lower bound to zero.
    if (yMin > 0 && yMin <= 2) yMin = 0;
    if (xMin > 0 && xMin <= 2) xMin = 0;

    return {xMin, xMax, yMin, yMax};
};

// Builds a single BufferGeometry for the complete panel with optional 45° miter
// cuts at each joining end (yMin and/or yMax).  When `bevelAtYMin` (or
// `bevelAtYMax`) is false the corresponding end is left square instead of
// mitered – used when no adjacent face panel is present on that side.
//
// Coordinate system is identical to the existing pipeline (shape in XY,
// extruded in –Z).  After panelGroup.rotateZ(PI/2) and scale.setZ(–1) the
// panel lands in the correct world position.
//
// Six faces, each with 4 dedicated vertices (no shared vertices) so UVs and
// normals are independent per face:
//
//   group 0 – front cap + back cap     (face / substrate material)
//   group 1 – left side, right side,
//              bottom end, top end      (edge material)
//
// c = panelDepth → both miter legs are equal → exact 45° when beveled.
// When an end is square the end face spans the full depth (z 0 → –c).
const buildMiteredPanelGeometry = (
    xMin: number,
    xMax: number,
    yMin: number,
    yMax: number,
    panelDepth: number,
    bevelAtYMin = true,
    bevelAtYMax = true
): THREE.BufferGeometry => {
    // Guard: keep a sliver of visible front-face height even on very short panels.
    const c = Math.min(panelDepth, (yMax - yMin) / 2 - 0.01);
    // Per-end inset: c when beveled, 0 when square.
    const cMin = bevelAtYMin ? c : 0;
    const cMax = bevelAtYMax ? c : 0;

    const height = yMax - yMin;

    // 6 faces × 4 unique vertices = 24 vertices
    const positions = new Float32Array(24 * 3);
    const uvs = new Float32Array(24 * 2);

    let vi = 0;
    const push = (x: number, y: number, z: number, u: number, v: number) => {
        positions[vi * 3] = x;
        positions[vi * 3 + 1] = y;
        positions[vi * 3 + 2] = z;
        uvs[vi * 2] = u;
        uvs[vi * 2 + 1] = v;
        vi++;
    };

    // Front face (z = 0) — group 0 (verts 0-3)
    // Inset by cMin / cMax so the end faces remain separate faces.
    push(xMin, yMin + cMin, 0, 0, 0); // 0
    push(xMax, yMin + cMin, 0, 1, 0); // 1
    push(xMax, yMax - cMax, 0, 1, 1); // 2
    push(xMin, yMax - cMax, 0, 0, 1); // 3

    // Back face (z = -c) — group 0 (verts 4-7)
    // Full y extent – the miter/square cut is only on the front-face side.
    push(xMin, yMin, -c, 0, 0); // 4
    push(xMax, yMin, -c, 1, 0); // 5
    push(xMax, yMax, -c, 1, 1); // 6
    push(xMin, yMax, -c, 0, 1); // 7

    // Left side (x = xMin, trapezoid) — group 1 (verts 8-11)
    // U maps along panel length, V maps across the depth.
    push(xMin, yMin, -c, 0, 0); //  8
    push(xMin, yMin + cMin, 0, cMin / height, 1); //  9
    push(xMin, yMax - cMax, 0, (height - cMax) / height, 1); // 10
    push(xMin, yMax, -c, 1, 0); // 11

    // Right side (x = xMax, trapezoid) — group 1 (verts 12-15)
    push(xMax, yMin, -c, 0, 0); // 12
    push(xMax, yMin + cMin, 0, cMin / height, 1); // 13
    push(xMax, yMax - cMax, 0, (height - cMax) / height, 1); // 14
    push(xMax, yMax, -c, 1, 0); // 15

    // Bottom end (yMin): 45° miter when bevelAtYMin, square otherwise
    push(xMin, yMin + cMin, 0, 0, 0); // 16
    push(xMax, yMin + cMin, 0, 1, 0); // 17
    push(xMax, yMin, -c, 1, 1); // 18
    push(xMin, yMin, -c, 0, 1); // 19

    // Top end (yMax): 45° miter when bevelAtYMax, square otherwise
    push(xMin, yMax - cMax, 0, 0, 0); // 20
    push(xMax, yMax - cMax, 0, 1, 0); // 21
    push(xMax, yMax, -c, 1, 1); // 22
    push(xMin, yMax, -c, 0, 1); // 23

    // 12 triangles = 36 indices
    // Group 0 (front + back):  indices  0..11
    // Group 1 (sides + ends):  indices 12..35
    const idx = new Uint16Array([
        // Front face — normal +Z
        0, 1, 2, 0, 2, 3,
        // Back face  — normal -Z (reversed winding)
        4, 6, 5, 4, 7, 6,
        // Left side  — normal -X
        8, 9, 11, 9, 10, 11,
        // Right side — normal +X
        12, 15, 14, 12, 14, 13,
        // Bottom end (miter or square)
        16, 18, 17, 16, 19, 18,
        // Top end    (miter or square)
        20, 21, 22, 20, 22, 23,
    ]);

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    geo.setIndex(new THREE.BufferAttribute(idx, 1));
    geo.addGroup(0, 12, 0); // front + back caps → face material
    geo.addGroup(12, 24, 1); // sides + end faces → edge material
    geo.computeVertexNormals();
    return geo;
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

// Replacement for createPanelGeometry for kicker / bulkhead panels.
//
// Uses a single custom geometry (buildMiteredPanelGeometry) that contains
// optional 45° miter cuts at each joining end, so EdgesGeometry produces one
// clean set of edge lines without the box/chamfer-strip overlap artefacts.
//
// Pass `faceFlags` to suppress miters at ends where no adjacent face panel
// exists.  When omitted all ends are mitered (backwards-compatible default).
//
// The transform pipeline (rotateZ, scale) is identical to createPanelGeometry
// so the panel lands in the same world position.
export const createBeveledPanelGeometry = (
    manager: THREE.LoadingManager | undefined,
    operationsGroup: OperationGroup,
    color = '#000',
    showTexture = false,
    materialImage: string | null = null,
    shouldApplyMaterial = false,
    isExteriorMaterial = false,
    thickness: {exteriorThickness: number; carcaseThickness: number} = {
        exteriorThickness: 16.5,
        carcaseThickness: 16.5,
    },
    isWireframeMode = false,
    // kept for API compatibility (was used for curveSegments in ExtrudeGeometry)
    _operationCount = 0,
    faceFlags?: BevelFaceFlags,
    horizontalGrainExt = false,
    horizontalGrainCarc = false
): THREE.Group => {
    void _operationCount;

    const panelDepth = isExteriorMaterial
        ? thickness.exteriorThickness
        : thickness.carcaseThickness;

    const textureLoader = new THREE.TextureLoader(manager);
    const segmentMaterial = new THREE.LineBasicMaterial({color});
    // Fade the outlines like the other panels while the faces are drawn;
    // they go fully opaque in wireframe, where they're the only thing drawn.
    if (showTexture) {
        setSegmentLineTransparency(segmentMaterial, isWireframeMode);
    }
    const panelGroup = new THREE.Group();

    let hasPerimeterShape = false;
    for (const op of operationsGroup.Operations) {
        if (op.Type === 'perimeter_shape') {
            hasPerimeterShape = true;
            break;
        }
    }

    for (const operation of operationsGroup.Operations) {
        if (operation.Type === 'panel' && hasPerimeterShape) continue;
        if (operation.Type === 'system32' || operation.Type === 'drilled_hole')
            continue;
        if (!operation.Segments) continue;

        const {xMin, xMax, yMin, yMax} = getShapeBounds(
            operation as Parameters<typeof getShapeBounds>[0]
        );

        // Determine per-end bevel flags from the assembly's face layout.
        const {bevelAtYMin, bevelAtYMax} = faceFlags
            ? getPanelBevelEnds(operationsGroup, faceFlags)
            : {bevelAtYMin: true, bevelAtYMax: true};

        // One geometry for the full panel including miter/square cuts at each end.
        const panelGeo = buildMiteredPanelGeometry(
            xMin,
            xMax,
            yMin,
            yMax,
            panelDepth,
            bevelAtYMin,
            bevelAtYMax
        );

        // EdgesGeometry on the single geometry → no duplicate / artefact lines.
        // 30° threshold: all face junctions (45° or 90° dihedral) are captured.
        const edgeGeo = new THREE.EdgesGeometry(panelGeo, 30);

        segmentMaterial.visible = false;

        if (showTexture) {
            const faceMat = new THREE.MeshStandardMaterial({color: '#F7F8F5'});
            const edgeMat = new THREE.MeshStandardMaterial({
                color: '#F7F8F5',
                side: THREE.DoubleSide,
            });

            const panelMesh = new THREE.Mesh(panelGeo, [faceMat, edgeMat]);
            addDragEffectOnClick(
                panelMesh as unknown as THREE.Mesh<
                    THREE.BoxGeometry,
                    THREE.MeshStandardMaterial | THREE.MeshStandardMaterial[]
                >,
                segmentMaterial
            );
            if (isWireframeMode) {
                panelMesh.visible = false;
            }

            panelGroup.add(panelMesh);
            segmentMaterial.visible = true;

            if (materialImage && shouldApplyMaterial) {
                void (async () => {
                    try {
                        const texture = await loadTexture(
                            textureLoader,
                            materialImage
                        );
                        texture.generateMipmaps = true;
                        texture.minFilter = THREE.LinearMipmapLinearFilter;
                        texture.magFilter = THREE.LinearFilter;
                        // texture.wrapS = texture.wrapT = THREE.RepeatWrapping;

                        // Apply grain orientation based on material type
                        const hasHorizontalGrain = isExteriorMaterial
                            ? horizontalGrainExt
                            : horizontalGrainCarc;
                        texture.rotation = hasHorizontalGrain
                            ? 0
                            : (Math.PI / 4) * 2;
                        texture.center = new THREE.Vector2(0.5, 0.5);
                        texture.needsUpdate = true;

                        const mat = new THREE.MeshStandardMaterial({
                            map: texture,
                        });
                        // Apply same texture to both face and edge material groups.
                        panelMesh.material = [mat, mat];
                    } catch {
                        // keep placeholder on failure
                    }
                })();
            } else {
                notifyManagerInstantLoad(textureLoader, 'panel:no-texture');
            }
        } else {
            segmentMaterial.visible = true;
        }

        const lineMesh = new THREE.LineSegments(edgeGeo, segmentMaterial);
        if (showTexture) {
            attachSegmentLineWireframeToggle(lineMesh);
        }
        panelGroup.add(lineMesh);
    }

    // Replicate the transform pipeline from createPanelGeometry exactly.
    panelGroup.rotateZ(Math.PI / 2);
    if (operationsGroup.MfgOrientationID === undefined) {
        panelGroup.scale.setY(-1);
    }
    panelGroup.scale.setZ(-1);

    const ret = new THREE.Group();
    ret.add(panelGroup);
    return ret;
};
